import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { Department, DepartmentFormProps } from './Department.d'
import { Card } from 'primereact/card'
import { api } from '../../../../../auth'
import Common from '../../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import PageHeader from '../../../../layout/page/PageHeader'
import { Language } from '../../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../../common/forms/fields/TextField'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../../common/forms/errors/RenderFieldsErrors'
import RenderTranslatedFields from '../../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import {
    getGroupTranslation,
    getTranslation,
} from '../../../../common/utility/helpers'
import FormActions from '../../../../common/forms/FormActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../../common/forms/errors/RenderFormikErrors'

const DepartmentForm: FC<DepartmentFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'DEPARTMENT_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyDepartment: Department = {
        name: '',
        sub_header: '',
        order: null,
    }

    const [departmentDialog, setDepartmentDialog] = useState(true)
    const [department, setDepartment] = useState<Department>(emptyDepartment)
    const [recordName, setRecordName] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema: Record<string, any> = {
        name: Yup.string().trim().required('Required'),
    }

    let _initialValues: Record<string, any> = {
        ...department,
        name: pageCode + '_NAME',
        sub_header: pageCode + '_SUB_HEADER',
    };

    (Object.values(initData.languages.data) as Language[]).map((lang) => {
        schema[`name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Name (${lang.name}) field is required`)
        _initialValues[`name_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const DepartmentFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/departments/' + id
            : '/departments/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data?.department) {
                    setDepartment(response.data.data.department)
                    setRecordName(
                        getTranslation(
                            response.data.data.translations,
                            response.data.data.department.name,
                            initData.languages.default.locale
                        )
                    )
                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.department,
                        }
                    })
                    Object.keys(initData.languages.data).map((locale) => {
                        setDepartment((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations: Record<string, any> = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'name',
                                        code: response.data.data.department
                                            .name,
                                    },
                                    {
                                        key: 'sub_header',
                                        code: response.data.data.department
                                            .sub_header,
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`name_${lang.id}`]:
                                        groupTranslations['name'],
                                    [`sub_header_${lang.id}`]:
                                        groupTranslations['sub_header'],
                                }
                            })

                            return {
                                ...prevState,
                                [`name_${lang.id}`]: groupTranslations['name'],
                                [`sub_header_${lang.id}`]:
                                    groupTranslations['sub_header'],
                            }
                        })

                        return true
                    })
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load department')
            }
        }
    }, [axiosPrivate, id, initData.languages, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/departments/' + id
                : '/departments/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setDepartmentDialog(false)
        navigate('/people/team/departments')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/departments/delete/' + id}
                    record={department}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete this department?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'department-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={DepartmentFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="department-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={12}>
                                                <RenderTranslatedFields
                                                    id="teamMember"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'formDepartmentName',
                                                            type: 'text',
                                                            name: 'name',
                                                            label: 'Name',
                                                            placeholder:
                                                                'Enter name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formDepartmentSubHeader',
                                                            type: 'text',
                                                            name: 'sub_header',
                                                            label: 'Sub Header',
                                                            placeholder:
                                                                'Enter Sub Header',
                                                        },
                                                    ]}
                                                />
                                                <Row>
                                                    <Col xl={6}>
                                                        <TextField
                                                            id="formDepartmentOrder"
                                                            name="order"
                                                            label="Order on Website"
                                                            placeholder="Enter order"
                                                            type="number"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="department-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={departmentDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '50vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + recordName
                                : 'Create new department'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + recordName
                                : 'Create new department'
                        }
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default DepartmentForm
